import React, {useContext, useEffect, useMemo} from 'react';
import {useQuery, useQueryClient} from '@tanstack/react-query';
import {CarecoApiContext} from '@/app-context/careco-api-context';
import {useStateConfig} from '@/hooks/useStateConfig';
import {Client, RosterResponse} from '@/api/dto/dto';
import {EventDate, EventType} from "@clients/ClientsTable.styles";
import {useRosterStore} from "@/store/roster_store";
import PermissionsHelpers from "@/helpers/permissions_helpers";
import {useAuthStore} from "@/store/auth_store";
import DisplayHelpers from "@/helpers/display_helpers";
import display_helpers from "@/helpers/display_helpers";
import SortableTable, {CurrentSort, SORT_ORDER, TableColumn, TableData} from "@common/SortableTable";
import AssignedToClient from "@clients/AssignedToClient";
import {
  DateRangeFilterOption,
  FilterQuery,
  groupedOption,
  MultiselectSearchFilterOption,
  SingleSelectFilterOption,
  nonGroupedValues
} from '@common/FilterComponent';
import {SearchParam} from "@/helpers/search_params";
import {FilterView} from "@/api/dto/filters";
import {TableName} from "@/api/dto/mixpanel";
import {TableLink} from "@common/TableLink.styles";
import {EncounterType, EventFilterOptions} from "@/api/dto/client-profile";
import {
  displayValueToGroupTypes,
  GroupTypes
} from "@clients/profile_page/summary_tab/timeline_components/ADTCard.constants";
import RiskIndicators from "@clients/RiskIndicators";
import {SortOrderType} from "@clients/ClientsPage";
import {useEventFilterPermissions} from "@/hooks/useEventFilterPermissions";
import {toUIStatusLabel} from "@clients/profile_page/summary_tab/timeline_components/Encounter.constants";

type Props = {
  clients: (Client | undefined)[];
  searchTotal: number;
  rosterClientsTotal: number;
  loading: boolean;
  fetching?: boolean;
  loadError: Error | null;
  searchTerm: string;
  searchFunction: (searchTerm: string) => void;
  query?: FilterQuery;
  sortFunction?: (sortKey: TableColumn, sortOrder: SortOrderType) => void;
  resetSortFunction?: (invalidate: boolean) => void;
  handleSearchByChange?: (currentSearchBy: string) => void;
  currentSearchBy?: string;
  onNextPage?: () => void;
  currentFilters?: Record<string, unknown>;
  setCurrentFilters?: (filters: Record<string, unknown>, rosterId?: number) => void;
}

const tableHeaders = [
  "Name",
  "Client ID",
  "Date of Birth",
  "Risk",
  "Last Event",
  "Last Event Status",
  "Assigned to"
];

export enum ClientsSearchBy {
    NAME = 'name',
    CLIENT_ID = 'clientId',
}

const searchByLabels: Record<ClientsSearchBy, string> = {
  [ClientsSearchBy.NAME]: 'Search by client name',
  [ClientsSearchBy.CLIENT_ID]: 'Search by client ID',
};

// Static object to prevent creating a new instance on each render,
// which would otherwise trigger useEffect hooks unnecessarily.
const initialSortObj : CurrentSort = {
  tableColumn: {
    tableHeader: 'Last Event',
    key: 'lastEvent',
    type: 'string',
    value: '',
    displayValue: ''
  },
  sortOrder: SORT_ORDER.DESC
};

const ClientsTable = (props: Props) => {
  const { carecoApi } = useContext(CarecoApiContext);
  const stateConfig = useStateConfig();
  const currentRoster = useRosterStore((state) => state.currentRoster);
  const currentUser = useAuthStore(((state) => state.currentUser));
  const [resetTableFilters, setResetTableFilters] = React.useState<boolean>(false);
  const permissions = useEventFilterPermissions();
  const placeholder =
      props.currentSearchBy === ClientsSearchBy.NAME
        ? searchByLabels.name
        :  searchByLabels.clientId;
  const { data: assignedToFilterOptions } = useQuery({
    queryKey: ['assignedToFilterOptions', currentRoster?.id],
    queryFn: () => carecoApi?.getUsersAssignedToClients(currentRoster!.id).then(users =>
      users.map((user): nonGroupedValues => ({
        id: user.id,
        value: user.id,
        label: `${user.first_name} ${user.last_name}`,
      })),
    ),
  });
  const {data: rosters} = useQuery({queryKey: ['rosters']});
  const queryClient = useQueryClient();
  const mountedRef = React.useRef(false);

  const appliedFiltersFromCache = React.useMemo(() => {
    return queryClient.getQueryData<{
      rosterId: number;
      filters: Record<string, unknown>;
      timestamp: string;
    }>(['appliedFilters']);
  }, [queryClient]);

  useEffect(() => {
    if (appliedFiltersFromCache?.filters && props.setCurrentFilters && currentRoster?.id === appliedFiltersFromCache.rosterId) {
      const currentF = props.currentFilters || {};
      const hasFilters = Object.keys(currentF).some(
        key => {
          const val = currentF[key];
          if (Array.isArray(val)) return val.length > 0;
          if (val && typeof val === 'object') return Object.keys(val as Record<string, unknown>).length > 0;
          return !!val;
        }
      );

      if (!hasFilters) {
        props.setCurrentFilters(appliedFiltersFromCache.filters);
        mountedRef.current = true;
      }
    }
  }, [appliedFiltersFromCache, props, currentRoster?.id]);

  useEffect(() => {
    if (!mountedRef.current) {
      mountedRef.current = true;
      return;
    }

    if (currentRoster?.id && props.query?.params) {
      const hasFilters = Object.values(props.query.params).some(val => {
        if (Array.isArray(val)) return val.length > 0;
        if (val && typeof val === 'object') return Object.keys(val as Record<string, unknown>).length > 0;
        return !!val;
      });

      if (hasFilters) {
        queryClient.setQueryData(
          ['appliedFilters'],
          {
            rosterId: currentRoster.id,
            filters: props.query.params,
            timestamp: new Date().toISOString(),
          }
        );
      } else {
        queryClient.setQueryData(['appliedFilters'], null);
      }
    }
  }, [currentRoster?.id, props.query?.params, queryClient]);

  const roster = (rosters as RosterResponse)?.data?.data
    .find(roster => roster.id === currentRoster?.id);
  const groupTypeDisplayName = roster ? roster.typeDisplayName ?? 'ADT' : 'ADT';

  const tableData: TableData[] = useMemo(() => props.clients.map((client): TableData => {
    const clientName = client ? DisplayHelpers.formatName(client.firstName, client.middleName, client.lastName) : "";
    const data: TableData = {
      name: {
        tableHeader: 'Name',
        key: 'name',
        type: 'string',
        value: clientName,
        displayValue: (
          !!currentUser && !!currentRoster && PermissionsHelpers.hasRosterAccess(currentUser, currentRoster)
            ? <TableLink
              to={`/clients/profile?${SearchParam.PATIENT_ID}=${client?.patientId}&${SearchParam.ROSTER_ID}=${currentRoster?.id}`}
            >
              {clientName}
            </TableLink>
            : clientName
        ),
        columnWidth: '20%',
      },
      clientId: {
        tableHeader: 'Client ID',
        key: 'clientId',
        type: 'string',
        value: client?.clientId ?? '',
        displayValue: client?.clientId,
        columnWidth: '10%',
      },
      dob: {
        tableHeader: 'Date of Birth',
        key: 'dob',
        type: 'string',
        value: client?.dateOfBirth ?? '',
        displayValue: client && client.dateOfBirth ? DisplayHelpers.formatDateOfBirthWithAge(client.dateOfBirth) : "",
        columnWidth: '15%',
      },
      risk: {
        tableHeader: 'Risk',
        key: 'risk',
        type: 'custom',
        value: '',
        displayValue: <RiskIndicators riskFlags={client?.riskFlags}/>,
        columnWidth: '10%',
      },
      lastEvent: {
        tableHeader: 'Last Event',
        key: 'lastEvent',
        type: 'string',
        value: '',
        displayValue: (
          client?.lastEventType && (
            <>
              <EventType>
                {client.lastEventType === EncounterType.ADT && client.lastEventGroupType
                  ? `${GroupTypes[client.lastEventGroupType]?.displayName
                  ?? groupTypeDisplayName} event`
                  : `${display_helpers.readableEncounterType(client.lastEventType, stateConfig)} event`}
              </EventType>
              <br />
              <EventDate>{DisplayHelpers.formatDate(client.lastEventDate)}</EventDate>
            </>
          )
        ),
        columnWidth: '15%',
      },
      lastEventStatus: {
        tableHeader: 'Last Event Status',
        key: 'lastEventStatus',
        type: 'string',
        value: client?.lastEventStatus ?? '',
        displayValue: toUIStatusLabel(client?.lastEventStatus ?? 'UNKNOWN'),
        columnWidth: '15%',
      },
      assignedTo: {
        tableHeader: 'Assigned to',
        key: 'assignedTo',
        type: 'custom',
        value: '',
        displayValue: client ? <AssignedToClient client={client} roster={currentRoster} /> : '',
        columnWidth: '15%',
      }
    };

    return {
      name: data.name,
      clientId: data.clientId,
      dob: data.dob,
      risk: data.risk,
      lastEvent: data.lastEvent,
      lastEventStatus: data.lastEventStatus,
      assignedTo: data.assignedTo
    };
  }), [props.clients]);

  const filterOptions: (MultiselectSearchFilterOption | SingleSelectFilterOption | DateRangeFilterOption)[] = [
    {
      type: 'multiselect-search',
      label: 'Assignee',
      dataType: 'ajax',
      options: assignedToFilterOptions ?? [],
      key: 'assignedTo',
      query: props.query!
    },
    {
      type: 'multiselect-search',
      label: 'Events',
      dataType: 'ajax',
      options: Object.entries(EventFilterOptions(stateConfig!, permissions))
        .map(([_, group]) => ({
          label: group.label,
          value: group.value,
          options: group.statuses.map((status) => ({
            id: `${group.value}_${status.value}`,
            value: `${group.value}:${
              (Object.keys(GroupTypes).includes(group.value) || Object.keys(displayValueToGroupTypes).includes(group.value))
                ? 'ADT' 
                : group.value }_${status.value}`
              .toUpperCase(),
            label: status.label,
            group: group.label,
          }))
        }))
        .sort((a, b) => a?.label?.localeCompare(b?.label)) as groupedOption[],
      key: 'events',
      query: props.query!
    },
    {
      type: 'date-range',
      label: 'Start Date',
      endLabel: 'End Date',
      dataType: 'ajax',
      key: 'eventDate',
      query: props.query!
    },
    {
      type: 'single-select',
      label: 'Admission status',
      dataType: 'ajax',
      options: [
        { id: 'actively_admitted', label: 'Actively admitted', value: 'actively_admitted' },
        { id: 'recently_discharged', label: 'Recently discharged', value: 'recently_discharged' }
      ] as nonGroupedValues[],
      key: 'admissionStatus',
      query: props.query!
    },
  ];

  const initialSort = useMemo(() => {
    if(tableData[0]) {
      initialSortObj['tableColumn'] = tableData[0].lastEvent;
      return initialSortObj;
    }
    return null;
  }, [tableData[0]]);

  // Create initialFilters from cached filters (similar to UsersTab)
  const initialFilters = React.useMemo(() => {
    if (!appliedFiltersFromCache?.filters) {
      return undefined;
    }

    const cachedFilters = appliedFiltersFromCache.filters;
    const transformedFilters: Record<string, unknown> = {};

    if (Array.isArray(cachedFilters.assignedTo) && cachedFilters.assignedTo.length > 0) {
      transformedFilters.assignedTo = (assignedToFilterOptions || []).filter(option =>
        (cachedFilters.assignedTo as string[]).includes(option.value)
      );
    }

    if (Array.isArray(cachedFilters.events) && cachedFilters.events.length > 0) {
      const allEventOptions: any[] = [];
      Object.entries(EventFilterOptions(stateConfig!, permissions))
        .forEach(([_, group]) => {
          group.statuses.forEach((status) => {
            allEventOptions.push({
              id: `${group.value}_${status.value}`,
              value: `${group.value}:${
                (Object.keys(GroupTypes).includes(group.value) || Object.keys(displayValueToGroupTypes).includes(group.value))
                  ? 'ADT'
                  : group.value }_${status.value}`
                .toUpperCase(),
              label: status.label,
              group: group.label,
            });
          });
        });

      transformedFilters.events = allEventOptions.filter(option =>
        (cachedFilters.events as string[]).includes(option.value)
      );
    }

    if (cachedFilters.eventDate && typeof cachedFilters.eventDate === 'object') {
      transformedFilters.eventDate = cachedFilters.eventDate;
    }

    if (Array.isArray(cachedFilters.admissionStatus) && cachedFilters.admissionStatus.length > 0) {
      const admissionStatusOptions = [
        { id: 'actively_admitted', label: 'Actively admitted', value: 'actively_admitted' },
        { id: 'recently_discharged', label: 'Recently discharged', value: 'recently_discharged' }
      ];
      transformedFilters.admissionStatus = admissionStatusOptions.filter(option =>
        (cachedFilters.admissionStatus as string[]).includes(option.value)
      );
    }

    return Object.keys(transformedFilters).length > 0 ? transformedFilters : undefined;
  }, [appliedFiltersFromCache, assignedToFilterOptions, stateConfig, permissions]);

  return (
    <>
      <SortableTable
        id={FilterView.CLIENTS_TABLE}
        data={tableData}
        tabName={TableName.CLIENT_LIST}
        loading={props.loading}
        fetching={props.fetching}
        loadError={props.loadError}
        header={tableHeaders}
        searchPlaceholder={placeholder ?? 'Search by client name'}
        resetTableFilters={resetTableFilters}
        setResetTableFilters={setResetTableFilters}
        searchColumns={[]}
        sortableColumns={tableData.length > 0 ? [
          tableData[0]?.name,
          tableData[0]?.lastEvent
        ] : []}
        filterOptions={props.query && filterOptions}
        tableOverrides={{
          searchFunction: props.searchFunction,
          searchTerm: props.searchTerm,
          count: props.rosterClientsTotal,
          sortFunction: props?.sortFunction,
          filteredCount: props.searchTotal,
          resetSortFunction: props?.resetSortFunction,
          initialSort,
          initialFilters
        }}
        columnsToResetOnSort={tableData.length > 0 ? [
          tableData[0]?.name
        ] : []}
        searchBy={[
          { label: searchByLabels.name, value: ClientsSearchBy.NAME },
          { label: searchByLabels.clientId, value: ClientsSearchBy.CLIENT_ID }
        ]}
        handleSearchByChange={props.handleSearchByChange}
        searchDropdownPlaceholder={props.currentSearchBy === ClientsSearchBy.NAME ?
          'Client name' : 'Client ID'}
        onNextPage={props.onNextPage}
      />
    </>
  );
};

export default ClientsTable;
